import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { Autocomplete, Button, CameraAltIcon, CameraIcon, Card, CardContent, CheckIcon, ClearIcon, DialogActions, DialogContent, Fab, Grid, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "../../styles/MaterialUI";
import ExpandableScreenAlertCard from '../OtherComponents/ExpandableScreenAlertCard';
import MultiSelect from '../MultiSelect';
import Webcam from 'react-webcam';
import ToggleSwitch from '../StyledComponents/Switch';
import ScreenCastingHistory from '../ScreenCastingHistory/ScreenCastingHistory';
import { useSelector } from 'react-redux';
import axios from 'axios';
import apiConstants from '../../constants/apiConstants';
import { buildContentTypeOptions } from '../../utils/contentTypeUtils';
import ToolTip from '../../utils/CursorTooltip'
import { useOrgConfigData } from "../../hooks/useOrgConfigData";
const EditScreenDialog = ({ tableData, showAlert, updateScreen, closeEditScreenModal }) => {
    const { screen_uiid, screen_name, location, building, floor, room, tags, thumbnail_name, banner_Visibility, screen_status, progressbar_visibility, dashboard_boilerplate_visibility } = useSelector((state) => state.editScreenData);
    const { userToken } = useSelector((state) => state.userToken);


    // Inside the component, after existing useSelector calls:
    const { isInternalUserMapped } = useSelector((state) => state.userLogin);
    const orgConfig = useOrgConfigData("editScreen");

    const {
        org_properties: org_configurations,
        all_content_types,
        permitted_content_types
    } = orgConfig || {};
    const allContentTypes = all_content_types ?? [];
    const permittedContentTypes = permitted_content_types ?? [];
    const headers = { Authorization: `Bearer ${userToken}` };
    const [updateScreenName, setUpdateScreenName] = useState(screen_name);
    const [updateScreenError, setUpdateScreenError] = useState(null);
    const [updateLocation, setUpdateLocation] = useState(location);
    const [updateBuilding, setUpdateBuilding] = useState(building);
    const [updateFloor, setUpdateFloor] = useState(floor);
    const [updateRoom, setUpdateRoom] = useState(room);
    const [updateThumbnail, setUpdateThumbnail] = useState("");
    const [changes, setChanges] = useState({ screen: false, mapping: false, screenStatus: null });
    const [rowData, setRowData] = useState({});
    const [locationList, setLocationList] = useState([]);
    const [buildingList, setBuildingList] = useState([]);
    const [floorList, setFloorList] = useState([]);
    const [roomList, setRoomList] = useState([]);
    const [addTags, setAddTags] = useState(tags);
    const [tagList, settagList] = useState([]);
    const [webcamOpen, setWebcamOpen] = useState(false);
    const webcamRef = useRef(null);
    const [thumbnailSrc, setThumbnailSrc] = useState(null);
    const [thumbnailName, setThumbnailName] = useState(thumbnail_name);
    const [bannerVisibility, setBannerVisibility] = useState(banner_Visibility);
    const [progressBarVisibility, setProgressBarVisibility] = useState(progressbar_visibility);
    const [dashboardBoilerPlateVisibility, setDashboardBoilerPlateVisibility] = useState(dashboard_boilerplate_visibility);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedContent, setSelectedContent] = useState({ id: "", name: "" });
    const [mapScreenNotes, setMapScreenNotes] = useState("");
    const [contentType, setContentType] = useState(null);
    const [contentList, setContentList] = useState([{ id: "", name: "" }]);
    const [mapScreenContentId, setMapScreenContentId] = useState("");
    const contentTypeNames = { "Image": "image", "Video": "video", "IntelliDash Dashboard": "dashboard", "Content Playlist": "content_playlist", "Machine Metrics": "orchestrated_url", "Acumatica": "orchestrated_url", "Power BI": "orchestrated_url", "Open URL": "open_url" }
    const mapScreenInputs = { id: [], screenname: [] };
    const [mapScreenModalInputs, setMapScreenModalInputs] = useState(mapScreenInputs);
    const [orgIntegrationData, setOrgIntegrationData] = useState([]);

    // RM 8277
    // ── Org-level permission flags ──────────────────────────────────────────
    const orgDisplay = org_configurations?.display ?? {};
    const orgLimits = org_configurations?.aristocast_limits ?? {};


    const hasOrgDisplay =
        orgDisplay &&
        Object.keys(orgDisplay).length > 0;

    const canHideBanner =
        hasOrgDisplay ? orgDisplay.show_banner !== false : false;

    const canHideProgress =
        hasOrgDisplay ? orgDisplay.show_progress_bar !== false : false;

    const canHideDashboard =
        hasOrgDisplay ? orgDisplay.show_dashboard_boilerplate !== false : false;

    const contentTypeOptions = useMemo(() => {
        const options = buildContentTypeOptions(
            allContentTypes,
            permittedContentTypes,
            orgIntegrationData
        );

        // const getId = (id) =>
        //     id ? Number(id.replace(/\D/g, "")) : 9999;

        const sorted = [...options].sort((a, b) => {
            // 1. enabled first
            // if (a.disabled !== b.disabled) {
            //     return a.disabled ? 1 : -1;
            // }

            // 2. optional: sort by ID inside each group
            return a.ic_content_type_name - b.ic_content_type_name;
        });

        return sorted;
    }, [allContentTypes, permittedContentTypes, orgIntegrationData]);


    const selectedTypeOption = contentTypeOptions.find((o) => o.label === contentType) ?? null;


    const handleUpdateScreenNameChange = (e) => {
        const { value } = e.target;
        setUpdateScreenName(value);
        setChanges((prev) => ({ ...prev, screen: true }));
    };

    const base64ToBlob = (base64Data, contentType) => {
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
    };

    // ── Toggle change handlers (respect org permission) ─────────────────────
    const handleChangeBannerStatus = (event) => {
        if (!canHideBanner) return; // locked — ignore interaction
        setBannerVisibility(event.target.checked);
        setChanges((prev) => ({ ...prev, screen: true }));
    }

    const handleChangeProgressBarStatus = (event) => {
        if (!canHideProgress) return;
        setProgressBarVisibility(event.target.checked);
        setChanges((prev) => ({ ...prev, screen: true }));
    }

    const handleChangeDashboardBoilerPlateStatus = (event) => {
        if (!canHideDashboard) return;
        setDashboardBoilerPlateVisibility(event.target.checked);
        setChanges((prev) => ({ ...prev, screen: true }));
    }

    const handleChangeContentType = (_, value) => {
        if (value && selectedContent?.id !== value?.id) {
            setSelectedContent(value);
            setMapScreenContentId(value.id);
            setChanges((prev) => ({ ...prev, mapping: true }));
        } else if (!value && selectedContent.id !== "") {
            setSelectedContent({ id: "", name: "" });
        }
    }

    const handleChangeType = async (value, contentId = "", contentName = "") => {
        setContentList([{ id: "", name: "" }]);
        setSelectedContent({ id: contentId, name: contentName });
        setContentType(value);
        setChanges((prev) => ({ ...prev, mapping: true }));

        const inputValue = contentTypeNames[value];
        try {
            const response = await axios.post(`${apiConstants.GET_API_URL}getContents`, { org_id: "test" }, { headers });

            const { data } = response.data;
            const filteredData = data.filter((item) => item.ic_content_type === inputValue && item.ic_content_status === 1);
            const selectedIntegration = await getIntegrationByName(value);
            let contentListNew = filteredData
                .filter(item => {
                    if (value === "IntelliDash Dashboard") {
                        return !item.ic_org_integration_id;
                    }
                   
                    if (selectedIntegration) {
                        return item.ic_org_integration_id === selectedIntegration.ic_org_integration_id;
                    }

                    return true; // fallback (e.g. "All")
                })
                .map(item => ({
                    id: item.ic_content_id,
                    name: item.ic_content_name
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            setContentList(contentListNew);
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    };

    const generateAlphabeticalList = (list = []) => {
        if (list.length === 0) {
            return [];
        }
        return list.sort((a, b) => a.localeCompare(b));
    };

    const fetchScreenAttributes = async () => {
        try {
            const response = await axios.post(`${apiConstants.GET_API_URL}getScreenAttributes`, {}, { headers });
            const { data } = response.data;

            setLocationList(generateAlphabeticalList(data.locations));
            setBuildingList(generateAlphabeticalList(data.buildings));
            setFloorList(generateAlphabeticalList(data.floors));
            setRoomList(generateAlphabeticalList(data.rooms));
            settagList(generateAlphabeticalList(data.tags));
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    };

    const validateScreenMapping = (data) => {
        return data.every(item => item.ic_display_status !== "Deactivated");
    };

    const handleScreenMapping = (screenuiid = []) => {
        const selectedData = tableData.filter((data) => screenuiid.includes(data.screen_uiid));
        const allowMapping = validateScreenMapping(selectedData);

        if (!allowMapping) {
            showAlert("error", "Mapping is only permitted for screens that are not deactivated");
        }
        const [data] = selectedData;

        if (data.ic_content_type !== null) {
            const contentType = data.ic_content_type;

            setMapScreenContentId(data.ic_content_id);
            handleChangeType(contentType, data.ic_content_id, data.ic_content_name);
        }

        setMapScreenModalInputs((prev) => ({
            ...prev,
            id: selectedData.map((data) => data.screen_uiid), screenname: selectedData.map((data) => data.ic_screen_name),
        }));
    };

    const mapDatatoScreen = () => {
        setRowData({ screen_uiid: screen_uiid });
        handleScreenMapping([screen_uiid]);
        setMapScreenNotes("");
        setChanges((prev) => ({ ...prev, screen: false, mapping: false, screenStatus: screen_status }));
    }

    const getIntegrationsData = async () => {
        const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getIntegrations`, {}, { headers });
        const { data } = apiResponse.data;
        setOrgIntegrationData(data);
    }

    const getIntegrationByName = async (name) => {
        if (orgIntegrationData && orgIntegrationData.length > 0) {
            return orgIntegrationData.find(
                (item) => item.integration_name === name
            );
        }
        else {
            const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getIntegrations`, {}, { headers });
            const { data } = apiResponse.data;
            setOrgIntegrationData(data);
            return data.find(
                (item) => item.integration_name === name
            );
        }

    };

    const isIntegrationActive = (name) => {
        return orgIntegrationData.some(
            (item) =>
                item.integration_name === name &&
                item.ic_activeflag === 1
        );
    };

    useEffect(() => {
        fetchScreenAttributes();
        mapDatatoScreen();
        getIntegrationsData();
    }, []);

    const handleCloseTag = async (selectedValue, customValue = "") => {
        const customTag = typeof customValue === 'string' ? customValue.trim() : "";
        setChanges((prev) => ({ ...prev, screen: true }));

        if (
            customTag && !tagList.map((tag) => tag.toLowerCase()).includes(customTag.toLowerCase())
        ) {
            try {
                const response = await axios.post(`${apiConstants.GET_API_URL}addTags`, { tagname: customTag }, { headers });

                if (response.data.statusCode === 200) {
                    setAddTags((prevTags) => [...prevTags, customTag]);
                    fetchScreenAttributes();
                }
            } catch (error) {
                const { statusCode, message, redirect_url } = error.response.data;
                showAlert("error", message);
                if (statusCode === 403) {
                    window.open(redirect_url, "_self");
                }
            }
        } else {
            setAddTags(selectedValue);
        }
    };

    const handleImageCapture = useCallback(() => {
        const src = webcamRef.current.getScreenshot();
        setThumbnailSrc(src);
        setWebcamOpen(false);
        setChanges((prev) => ({ ...prev, screen: true }));
    }, [webcamRef]);

    const videoConstraints = {
        facingMode: "environment",
    };

    const validateImageFormat = (event) => {
        const fileInput = event.target;

        if (fileInput.files.length > 0) {
            setChanges((prev) => ({ ...prev, screen: true }));
            const selectedFile = fileInput.files[0];
            const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];

            if (allowedFormats.includes(selectedFile.type)) {
                const fileSizeInMB = selectedFile.size / (1024 * 1024);
                if (fileSizeInMB > 10) {
                    showAlert("error", "File size exceeds 10MB. Please upload a smaller image");
                    fileInput.value = "";
                    setThumbnailName(null);
                    return;
                }
                setUpdateThumbnail(selectedFile);
                setThumbnailName(selectedFile.name);
            } else {
                showAlert("error", "Invalid file format. Please select an image file");
                fileInput.value = "";
                setThumbnailName(null);
            }
        } else {
            setThumbnailName(null);
        }
    };

    const updateMappingNotes = (event) => {
        const { value } = event.target;
        if (value.length <= 100) {
            setMapScreenNotes(value);
        }
    }

    const handleModifyScreenModal = async (e) => {
        e.preventDefault();
        if (!updateScreenName.trim()) {
            setUpdateScreenError("Screen Name cannot be empty.");
            return;
        }
        const newTag = addTags.filter(Boolean);
        const bannerStatus = bannerVisibility ? 0 : 1;
        const progressBarStatus = progressBarVisibility ? 0 : 1;
        const dashboardBoilerPlateStatus = dashboardBoilerPlateVisibility ? 0 : 1;

        const formData = new FormData();
        if (thumbnailSrc) {
            const base64Image = thumbnailSrc.split(';base64,').pop();
            const blob = base64ToBlob(base64Image, 'image/png');
            formData.append('thumbnail', blob, 'image.png');
        } else if (updateThumbnail) {
            formData.append("thumbnail", updateThumbnail);
        }

        formData.append("screen_uiid", screen_uiid);
        formData.append("updated_screen_name", updateScreenName.trim());
        formData.append("location", updateLocation);
        formData.append("building", updateBuilding);
        formData.append("floor", updateFloor);
        formData.append("room", updateRoom);
        formData.append("updated_tags", newTag);
        formData.append("hide_banner", bannerStatus);
        formData.append("hide_playlist_progress_bar", progressBarStatus);
        formData.append("hide_dashboard_boiler_plate", dashboardBoilerPlateStatus);

        const objectData = {
            contentType: contentType, contentName: selectedContent.name, screenuiid: mapScreenModalInputs.id, screen: changes.screen,
            mapping: changes.mapping, screenStatus: changes.screenStatus, contentID: mapScreenContentId, contentNotes: mapScreenNotes
        };

        updateScreen(formData, objectData, mapScreenModalInputs);
    };

    const resetEditScreenModal = () => {
        closeEditScreenModal();
    }

    // ── Toggle row helper ───────────────────────────────────────────────────
    /**
     * Renders a labelled toggle row.
     * When `permitted` is false the switch is locked to OFF ("Show") and
     * wrapped in a tooltip explaining the upgrade path.
     */
    const VisibilityToggleRow = ({ label, tooltip, checked, onChange, permitted }) => {
        const toggle = (
            <Stack
                direction="row"
                justifyContent="space-between"
                width="100%"
                sx={{ marginBottom: label === "Dashboard Boiler Plate" ? 0 : "-10px" }}
            >
                <Typography sx={{ marginTop: "5px", fontSize: "0.9rem", color: permitted ? "inherit" : "text.disabled" }}>
                    {label}
                </Typography>
                <ToggleSwitch
                    checked={permitted ? checked : true}   // locked → "Show"
                    handleChange={onChange}
                    disabled={!permitted}
                />
            </Stack>
        );

        return permitted ? toggle : (
            <ToolTip title={tooltip} >
                {toggle}
            </ToolTip>
        );
    };

    return (
        <>
            <form method="post" encType="multipart/form-data">
                <DialogContent style={{ maxHeight: 'calc(100vh - 200px)', overflowY: "auto" }} dividers>
                    <Grid container spacing={2}>
                        <ExpandableScreenAlertCard />
                        {/* Left Section */}
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Card
                                style={{
                                    overflow: "auto", padding: "10px",
                                    boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset'
                                }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <TextField variant="outlined" label="Screen Name" size="small" required value={updateScreenName} sx={{ width: "100%" }}
                                            onChange={(e) => handleUpdateScreenNameChange(e)} error={!!updateScreenError} helperText={updateScreenError} autoComplete="off"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        {!updateScreenError &&
                                                            updateScreenName.trim().length > 0 ? (
                                                            <CheckIcon color="success" sx={{ fontSize: "1.2rem" }} />
                                                        ) : ""}
                                                        {updateScreenError ? (
                                                            <ClearIcon color="error" sx={{ fontSize: "1rem" }} />
                                                        ) : ""}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <TextField variant="outlined" label="UIID" size="small" value={rowData.screen_uiid} disabled sx={{ width: "100%" }} />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <Autocomplete size="small" options={locationList} freeSolo value={updateLocation} style={{ width: "100%" }}
                                            onChange={(e, value) => {
                                                setUpdateLocation(value);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            onInputChange={(e, newInput) => {
                                                setUpdateLocation(newInput);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Location" />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <Autocomplete
                                            size="small" options={buildingList} freeSolo value={updateBuilding} style={{ width: "100%" }}
                                            onChange={(e, value) => {
                                                setUpdateBuilding(value);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            onInputChange={(e, newInput) => {
                                                setUpdateBuilding(newInput);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Building" />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <Autocomplete size="small" options={floorList} freeSolo value={updateFloor} style={{ width: "100%" }}
                                            onChange={(e, value) => {
                                                setUpdateFloor(value);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            onInputChange={(e, newInput) => {
                                                setUpdateFloor(newInput);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Floor" />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <Autocomplete size="small" options={roomList} freeSolo value={updateRoom} style={{ width: "100%" }}
                                            onChange={(e, value) => {
                                                setUpdateRoom(value);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            onInputChange={(e, newInput) => {
                                                setUpdateRoom(newInput);
                                                setChanges((prev) => ({ ...prev, screen: true }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Room" />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <MultiSelect tagList={tagList} onClose={handleCloseTag} selectedTag={addTags} />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        {webcamOpen && (
                                            <div style={{ position: "relative", display: "flex", justifyContent: "center", alignContent: "center", alignItems: "center" }}>
                                                <Webcam audio={false} ref={webcamRef} videoConstraints={videoConstraints} screenshotFormat="image/png" width={320} height={240} />
                                                <CameraIcon
                                                    sx={{
                                                        color: "#003366", fontSize: "3rem", position: "absolute", top: "90%", left: "50%", transform: "translate(-50%, -50%)",
                                                        "&:hover": {
                                                            cursor: "pointer",
                                                        },
                                                    }}
                                                    onClick={handleImageCapture}
                                                />
                                            </div>
                                        )}

                                        {thumbnailSrc && (
                                            <div>
                                                <h5>Captured Image</h5>
                                                <img src={thumbnailSrc} alt="captured" height={100} width={200} style={{ objectFit: "contain" }} />
                                            </div>
                                        )}

                                        <Typography sx={{ width: "50%" }}>Thumbnail</Typography>
                                        <Stack spacing={-1} direction="row" display="flex" justifyContent="space-between" alignItems="center"
                                            style={{ alignItems: "center", marginTop: "-25px", marginBottom: "-10px", objectFit: "fill" }}
                                        >
                                            <div>
                                                <Stack direction="row" display="flex" justifyContent="flex-start" alignItems="center">
                                                    <Stack>
                                                        <input className="upload-thumbnail1" type="file" name="thumbnail" accept="image/*" onChange={validateImageFormat} hidden />
                                                        <Stack direction="row" gap={1} display="flex" justifyContent="flex-start" alignItems="center">
                                                            <Fab size="small" variant="extended" onClick={() => document.querySelector(".upload-thumbnail1").click()}
                                                                sx={{
                                                                    width: "50%", textTransform: "none", backgroundColor: "#003366", color: "#fff", boxShadow: "none",
                                                                    "&:hover": {
                                                                        backgroundColor: "#003366"
                                                                    }, fontSize: "12px"
                                                                }}
                                                            >Choose File</Fab>
                                                            <span className="thumbnail-name" style={{ fontSize: "12px", display: "inline-block", maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{thumbnailName ? thumbnailName : "No file chosen"}</span>
                                                        </Stack>
                                                        <p style={{ fontSize: "0.8rem", marginTop: 2, whiteSpace: "nowrap" }}>Image size should not exceed 10 MB.</p>
                                                    </Stack>
                                                    <Tooltip title="Take a picture">
                                                        <IconButton edge="end" aria-label="Camera" size="small" onClick={() => setWebcamOpen(true)}
                                                            sx={{
                                                                backgroundColor: "#003366", color: "#fff",
                                                                "&:hover": {
                                                                    backgroundColor: "#0d45a5",
                                                                }, marginTop: "-30px !important"
                                                            }}
                                                        >
                                                            <CameraAltIcon style={{ fontSize: "20px" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </div>
                                            <div style={{ marginLeft: "2px", marginRight: "-8px" }}>
                                                <CardContent>
                                                    <VisibilityToggleRow
                                                        label="Banner"
                                                        checked={bannerVisibility}
                                                        onChange={handleChangeBannerStatus}
                                                        permitted={canHideBanner}
                                                        tooltip="Upgrade to Hide Banner"
                                                    />
                                                    <VisibilityToggleRow
                                                        label="Progress Bar"
                                                        checked={progressBarVisibility}
                                                        onChange={handleChangeProgressBarStatus}
                                                        permitted={canHideProgress}
                                                        tooltip="Upgrade to Hide Progress Bar"
                                                    />
                                                    <VisibilityToggleRow
                                                        label="Dashboard Boiler Plate"
                                                        checked={dashboardBoilerPlateVisibility}
                                                        onChange={handleChangeDashboardBoilerPlateStatus}
                                                        permitted={canHideDashboard}
                                                        tooltip="Upgrade to Hide Dashboard Boiler Plate"
                                                    />
                                                </CardContent>
                                            </div>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                        {/* Left Section */}

                        {/* Right Top Section */}
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Card
                                style={{
                                    maxHeight: "300px", overflow: "auto", padding: "10px",
                                    boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset'
                                }}
                            >
                                <h4 style={{ marginTop: 0, color: "#003366" }}>Content Mapping</h4>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                        <Autocomplete
                                            size="small"
                                            value={selectedTypeOption}
                                            options={contentTypeOptions}
                                            getOptionLabel={(option) => option?.label ?? ""}
                                            getOptionDisabled={(option) => option.disabled}
                                            isOptionEqualToValue={(option, value) =>
                                                typeof value === "string"
                                                    ? option.label === value
                                                    : option.label === value?.label
                                            }
                                            onChange={(e, newValue) => handleChangeType(newValue?.label ?? null)}
                                            renderOption={(props, option, { index }) => {
                                                const isIntegrationType = ["Machine Metrics", "Acumatica", "Power BI"].includes(option.label);
                                                const integrationInactive = isIntegrationType && !isIntegrationActive(option.label);

                                                const tooltipText = option.disabled
                                                    ? integrationInactive
                                                        ? "Integration not configured or inactive"
                                                        : "Upgrade to create this content type"
                                                    : "";

                                                // Show a divider right before the first disabled option in the list
                                                const isFirstDisabled =
                                                    option.disabled &&
                                                    (index === 0 || !contentTypeOptions[index - 1]?.disabled);

                                                return (
                                                    <React.Fragment key={option.label}>
                                                        {isFirstDisabled && (
                                                            <div style={{ borderTop: "0.5px solid #afa1a1", margin: "2px 0" }} />
                                                        )}
                                                        <ToolTip
                                                            title={tooltipText}
                                                            placement="right-end"
                                                            arrow
                                                            disableHoverListener={!option.disabled}
                                                            disableTouchListener={!option.disabled}
                                                        >
                                                            <span style={{ display: "block", width: "100%" }}>
                                                                <li
                                                                    {...props}
                                                                    aria-disabled={option.disabled}
                                                                    style={{
                                                                        ...props.style,
                                                                        color: option.disabled ? "#574d4d" : "inherit",
                                                                        cursor: option.disabled ? "not-allowed" : "pointer",
                                                                        pointerEvents: option.disabled ? "none" : "auto",
                                                                        opacity: option.disabled ? 0.6 : 1,
                                                                    }}
                                                                >
                                                                    {option.label}
                                                                </li>
                                                            </span>
                                                        </ToolTip>
                                                    </React.Fragment>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Content Type" />
                                            )}
                                            disabled={changes.screenStatus === "0"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                        <Autocomplete size="small" value={selectedContent || { id: "", name: "" }} options={contentList} onChange={handleChangeContentType} getOptionLabel={(option) => option?.name || ""}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Available Content for Mapping" InputLabelProps={{ style: { fontSize: isFocused || selectedContent?.name ? "1rem" : "0.75rem" } }}
                                                    onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} />
                                            )} disabled={changes.screenStatus === "0" || contentType === "Machine Metrics" && isIntegrationActive("Machine Metrics") !== true || contentType === "Acumatica" && isIntegrationActive("Acumatica") !== true || contentType === "Power BI" && isIntegrationActive("Power BI") !== true}
                                        />
                                    </Grid>
                                    {changes.mapping &&
                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                            <TextField value={mapScreenNotes} id="outlined-multiline" label="Notes" multiline rows={2} variant="outlined"
                                                onChange={updateMappingNotes} style={{ width: "100%" }} inputProps={{ maxLength: 100 }}
                                                helperText={`${mapScreenNotes ? mapScreenNotes.length : 0}/100 characters.`} />
                                        </Grid>
                                    }
                                </Grid>
                            </Card>

                            {/* Right Bottom Section */}
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Card
                                    style={{
                                        maxHeight: "300px", overflow: "auto", padding: "10px", marginTop: "20px", position: "relative",
                                        boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset'
                                    }}
                                >
                                    <h4 style={{ marginTop: "-10px", color: "#003366", padding: "10px 0", position: "sticky", top: "-10px", backgroundColor: "rgb(255, 255, 255)", zIndex: 1, }}>Casting History</h4>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                            <ScreenCastingHistory />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                            {/* Right Bottom Section */}
                        </Grid>
                        {/* Right Top Section */}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ marginRight: "20px", position: "sticky", bottom: 0 }}>
                    <Button type="submit" variant="contained" size="small" onClick={handleModifyScreenModal} disabled={isInternalUserMapped === 1}
                        sx={{
                            textTransform: "none", bgcolor: "#003366",
                            "&:hover": {
                                bgcolor: "#0d45a5",
                            },
                        }}
                    >
                        {changes.mapping ? "Update & Publish" : "Update"}
                    </Button>
                    <Button variant="contained" size="small" color="error" onClick={resetEditScreenModal} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </>
    )
}

export default EditScreenDialog;
